import { toast as showToast } from 'react-toastify';

export type emailValidationResponse = {
  isValid: boolean;
  errorMessage?: string;
};

// Known legitimate country-specific domains that fuzzy matching would otherwise flag as typos
const KNOWN_LEGITIMATE_DOMAINS = new Set([
  'yahoo.no',
  'yahoo.se',
  'yahoo.dk',
  'yahoo.fi',
  'yahoo.co.uk',
  'yahoo.co.jp',
  'yahoo.de',
  'yahoo.fr',
  'yahoo.es',
  'yahoo.it',
  'hotmail.no',
]);

// Common mistaken TLDs and their correct counterparts
const COMMON_TLD_MISTAKES: { [key: string]: string } = {
  // Gmail variations
  'gmail.co': 'gmail.com',
  'gmail.co.': 'gmail.com',
  'gmial.com': 'gmail.com',
  'gmai.com': 'gmail.com',
  'gmail.cm': 'gmail.com',
  'gmail.cmo': 'gmail.com',
  'gmail.con': 'gmail.com',
  'gmail.vom': 'gmail.com',
  'gmaill.com': 'gmail.com',
  'gmail.comm': 'gmail.com',
  'gmail.om': 'gmail.com',

  // Outlook/Hotmail variations
  'outlook.co': 'outlook.com',
  'outlook.cm': 'outlook.com',
  'outlook.con': 'outlook.com',
  'hotmail.co': 'hotmail.com',
  'hotmail.cm': 'hotmail.com',
  'hotmail.con': 'hotmail.com',

  // Yahoo variations
  'yahoo.co': 'yahoo.com',
  'yahoo.cm': 'yahoo.com',
  'yahoo.con': 'yahoo.com',
  'yahooo.com': 'yahoo.com',
  'yaho.com': 'yahoo.com',

  // Norwegian domains
  'gmail.no': 'gmail.com', // Gmail doesn't use .no

  // Generic TLD mistakes
  '.cm': '.com',
  '.con': '.com',
  '.comm': '.com',
  '.co.': '.com',
  '.vom': '.com',
  '.om': '.com',
  '.cmo': '.com',
  '.c': '.com',

  // Organization mistakes
  '.or': '.org',
  '.ogr': '.org',
  '.rog': '.org',

  // Network mistakes
  '.nte': '.net',
  '.ent': '.net',
  '.ten': '.net',
};

/**
 * Validates an email address format and checks for common TLD mistakes (SILENT - no toasts)
 * @param email - The email address to validate
 * @returns An object containing validation result and error message if any
 */
export const validateEmailSilent = (email: string): emailValidationResponse => {
  // Basic empty check
  if (!email || !email.trim().length) {
    const errorMessage = "Email can't be empty";
    return {
      isValid: false,
      errorMessage,
    };
  }

  const trimmedEmail = email.trim().toLowerCase();

  // Basic email format validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(trimmedEmail)) {
    const errorMessage = 'Please provide a valid email address format';
    return {
      isValid: false,
      errorMessage,
    };
  }

  // Extract domain part
  const emailParts = trimmedEmail.split('@');
  if (emailParts.length !== 2) {
    const errorMessage = 'Invalid email format';
    return {
      isValid: false,
      errorMessage,
    };
  }

  const [localPart, domain] = emailParts;

  // Check for specific domain mistakes
  const lowerDomain = domain.toLowerCase();

  // Check for exact domain matches
  if (COMMON_TLD_MISTAKES[lowerDomain]) {
    const suggestedCorrection = COMMON_TLD_MISTAKES[lowerDomain];
    const errorMessage = `Did you mean "${localPart}@${suggestedCorrection}"? Please check your email address.`;
    return {
      isValid: false,
      errorMessage,
    };
  }

  // Check for TLD-only matches (e.g., ending with .co instead of .com)
  for (const [mistake, correction] of Object.entries(COMMON_TLD_MISTAKES)) {
    if (mistake.startsWith('.') && lowerDomain.endsWith(mistake)) {
      const correctedDomain = lowerDomain.replace(
        new RegExp(`${mistake.replace('.', '\\.')}$`),
        correction,
      );
      const errorMessage = `Did you mean "${localPart}@${correctedDomain}"? Please check your email address.`;
      return {
        isValid: false,
        errorMessage,
      };
    }
  }

  // Check for common typing mistakes in popular domains
  const popularDomains = [
    'gmail.com',
    'outlook.com',
    'hotmail.com',
    'yahoo.com',
  ];
  if (KNOWN_LEGITIMATE_DOMAINS.has(lowerDomain)) {
    return { isValid: true };
  }
  for (const popularDomain of popularDomains) {
    if (isTypoOf(lowerDomain, popularDomain)) {
      const errorMessage = `Did you mean "${localPart}@${popularDomain}"? Please check your email address.`;
      return {
        isValid: false,
        errorMessage,
      };
    }
  }

  // Additional checks for Norwegian context
  if (lowerDomain.includes('eie.') && !lowerDomain.endsWith('eie.no')) {
    const errorMessage =
      'EIE email addresses should end with "eie.no". Please check your email address.';
    return {
      isValid: false,
      errorMessage,
    };
  }

  // Check for double dots or other obvious mistakes
  if (domain.includes('..') || domain.startsWith('.') || domain.endsWith('.')) {
    const errorMessage =
      'Invalid domain format. Please check your email address.';
    return {
      isValid: false,
      errorMessage,
    };
  }

  // Check for missing TLD
  if (!domain.includes('.')) {
    const errorMessage =
      'Email domain is missing a top-level domain (like .com, .org, .no)';
    return {
      isValid: false,
      errorMessage,
    };
  }

  // If all checks pass
  return {
    isValid: true,
  };
};

/**
 * Validates an email address format and checks for common TLD mistakes (WITH TOASTS)
 * Use this for form submissions where you want toast feedback
 * @param email - The email address to validate
 * @returns An object containing validation result and error message if any
 */
export const validateEmailWithToast = (
  email: string,
): emailValidationResponse => {
  const result = validateEmailSilent(email);

  // Show toast only if there's an error
  if (!result.isValid && result.errorMessage) {
    showToast.error(result.errorMessage);
  }

  return result;
};

/**
 * Default export - silent validation for backward compatibility and real-time validation
 * For form submissions where you want toasts, use validateEmailWithToast
 */
export const validateEmail = validateEmailSilent;

/**
 * Helper function to check if a domain is likely a typo of a popular domain
 * Using simple Levenshtein distance calculation
 */
const isTypoOf = (input: string, target: string): boolean => {
  if (Math.abs(input.length - target.length) > 2) return false;

  const distance = levenshteinDistance(input, target);
  // Allow 1-2 character differences for typos
  return distance <= 2 && distance > 0;
};

/**
 * Calculate Levenshtein distance between two strings
 */
const levenshteinDistance = (str1: string, str2: string): number => {
  const matrix = Array(str2.length + 1)
    .fill(null)
    .map(() => Array(str1.length + 1).fill(null));

  for (let i = 0; i <= str1.length; i += 1) {
    matrix[0][i] = i;
  }

  for (let j = 0; j <= str2.length; j += 1) {
    matrix[j][0] = j;
  }

  for (let j = 1; j <= str2.length; j += 1) {
    for (let i = 1; i <= str1.length; i += 1) {
      const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
      matrix[j][i] = Math.min(
        matrix[j][i - 1] + 1, // deletion
        matrix[j - 1][i] + 1, // insertion
        matrix[j - 1][i - 1] + indicator, // substitution
      );
    }
  }

  return matrix[str2.length][str1.length];
};

export default validateEmail;
